import * as React from 'react'
import { useCallback, useMemo } from 'react'

import DrillDownFilter from '~/client/src/shared/components/DrillDownFilter'
import type { IDrillDownFilterCategory } from '~/client/src/shared/components/DrillDownFilter/DrillDownFilter.types'
import {
  decodeDrillDownFilterSelectionId,
  encodeDrillDownFilterSelectionId,
} from '~/client/src/shared/components/DrillDownFilter/filterSelectionIds'
import MeCompanyQuickFilters from '~/client/src/shared/components/Filters/MeCompanyQuickFilters/MeCompanyQuickFilters'
import {
  RESOURCE_QUICK_FILTER_ME_KEY,
  RESOURCE_QUICK_FILTER_ME_OPTION_ID,
  RESOURCE_QUICK_FILTER_MY_COMPANY_KEY,
  RESOURCE_QUICK_FILTER_MY_COMPANY_OPTION_ID,
} from '~/client/src/shared/hooks/useResourceQuickFilters'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import {
  useMobileResourceActions,
  useMobileResourceMeta,
  useMobileResourceState,
} from '../context'

interface IMobileFacetItem {
  id: string
  label: string
  nodeLabel?: string
  count: number
}

const ME_SELECTION_ID = encodeDrillDownFilterSelectionId(
  RESOURCE_QUICK_FILTER_ME_KEY,
  RESOURCE_QUICK_FILTER_ME_OPTION_ID,
)
const MY_COMPANY_SELECTION_ID = encodeDrillDownFilterSelectionId(
  RESOURCE_QUICK_FILTER_MY_COMPANY_KEY,
  RESOURCE_QUICK_FILTER_MY_COMPANY_OPTION_ID,
)

export const MobileResourceFilterPicker: React.FC = () => {
  const { isFilterShown, appliedFilters } = useMobileResourceState()
  const { closeFilter, setFilter } = useMobileResourceActions()
  const { facets, getFacetOptions, quickFilters } = useMobileResourceMeta()

  const visibleFacets = useMemo(
    () => facets.filter(facet => !facet.isHiddenInFilterUI),
    [facets],
  )

  const categories = useMemo<IDrillDownFilterCategory<IMobileFacetItem>[]>(
    () =>
      visibleFacets.map(facet => {
        const facetOptions = getFacetOptions(facet.key)
        const items: IMobileFacetItem[] = facetOptions.map(option => ({
          id: option.id,
          label: option.label,
          nodeLabel: option.ownLabel,
          count: option.count,
        }))

        const parentByOptionId = new Map<string, string>()
        facetOptions.forEach(option => {
          if (option.parentValue) {
            parentByOptionId.set(option.id, option.parentValue)
          }
        })

        const renderItem = (
          item: IMobileFacetItem,
          _isSelected: boolean,
          label: string,
        ) => (
          <div className="tw:flex tw:items-center tw:justify-between tw:[&>*]:flex-none tw:w-full">
            <span className="tw:mr-2">{label}</span>
            <span className="tw:text-body-sm tw:text-muted-foreground tw:grow-0">
              {item.count}
            </span>
          </div>
        )

        const base = {
          id: facet.key,
          label: facet.label,
          items,
          getItemId: (item: IMobileFacetItem) =>
            encodeDrillDownFilterSelectionId(facet.key, item.id),
          getItemLabel: (item: IMobileFacetItem) => item.label,
          renderItem,
        }

        if (parentByOptionId.size === 0) {
          return base
        }

        return {
          ...base,
          kind: 'tree' as const,
          getItemParentId: (item: IMobileFacetItem) => {
            const parent = parentByOptionId.get(item.id)
            return parent
              ? encodeDrillDownFilterSelectionId(facet.key, parent)
              : null
          },
          getItemNodeLabel: (item: IMobileFacetItem) =>
            item.nodeLabel || item.label,
        }
      }),
    [visibleFacets, getFacetOptions],
  )

  // Quick filters are hidden facets, so their committed selection is folded
  // into the same pending set as the visible facets. Reset, the applied count,
  // and Apply then treat them like any other pending facet selection.
  const initialSelection = useMemo(() => {
    const facetIds = visibleFacets.flatMap(facet => {
      const selectedIds = appliedFilters[facet.key]
      if (!selectedIds?.size) return []
      return Array.from(selectedIds).map(id =>
        encodeDrillDownFilterSelectionId(facet.key, id),
      )
    })

    if (quickFilters.hasUserProperties && quickFilters.isOnlyMe) {
      facetIds.push(ME_SELECTION_ID)
    }
    if (quickFilters.hasCompanyProperties && quickFilters.isOnlyMyCompany) {
      facetIds.push(MY_COMPANY_SELECTION_ID)
    }

    return facetIds
  }, [
    appliedFilters,
    visibleFacets,
    quickFilters.hasUserProperties,
    quickFilters.isOnlyMe,
    quickFilters.hasCompanyProperties,
    quickFilters.isOnlyMyCompany,
  ])

  const handleApply = useCallback(
    (selectedIds: string[]) => {
      const nextSelectionByFacet: Record<string, string[]> = {}

      selectedIds.forEach(id => {
        const decoded = decodeDrillDownFilterSelectionId(id)
        if (!decoded) return

        if (!nextSelectionByFacet[decoded.facetKey]) {
          nextSelectionByFacet[decoded.facetKey] = []
        }
        nextSelectionByFacet[decoded.facetKey].push(decoded.optionId)
      })

      const managedKeys = visibleFacets.map(facet => facet.key)
      if (quickFilters.hasUserProperties) {
        managedKeys.push(RESOURCE_QUICK_FILTER_ME_KEY)
      }
      if (quickFilters.hasCompanyProperties) {
        managedKeys.push(RESOURCE_QUICK_FILTER_MY_COMPANY_KEY)
      }

      managedKeys.forEach(key => {
        setFilter(key, nextSelectionByFacet[key] || [])
      })
    },
    [
      visibleFacets,
      setFilter,
      quickFilters.hasUserProperties,
      quickFilters.hasCompanyProperties,
    ],
  )

  const hasQuickFilters =
    quickFilters.hasUserProperties || quickFilters.hasCompanyProperties

  return (
    <DrillDownFilter.Drawer
      open={isFilterShown}
      onOpenChange={next => {
        if (!next) closeFilter()
      }}
    >
      <DrillDownFilter.Mobile
        title={Localization.translator.filterBy}
        categories={categories}
        initialSelection={initialSelection}
        onApply={handleApply}
        headerContent={
          hasQuickFilters
            ? ({ isSelected, toggle }) => (
                <MeCompanyQuickFilters
                  isOnlyMe={isSelected(ME_SELECTION_ID)}
                  isOnlyMyCompany={isSelected(MY_COMPANY_SELECTION_ID)}
                  onToggleMe={() => toggle(ME_SELECTION_ID)}
                  onToggleMyCompany={() => toggle(MY_COMPANY_SELECTION_ID)}
                  showMe={quickFilters.hasUserProperties}
                  showMyCompany={quickFilters.hasCompanyProperties}
                  size="lg"
                />
              )
            : undefined
        }
      />
    </DrillDownFilter.Drawer>
  )
}
